import { Link, usePage } from '@inertiajs/react';
import { LogOut, User } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { csrfToken } from '@/lib/csrf';
import { cn, githubHandle } from '@/lib/utils';
import type { MentorProfile } from '@/types';
import { buildNavigation, isItemActive } from './sidebar-nav';

// The mentor portal sidebar — one component for the two
// app/(hack)/26-{co,mx}/mentor/(protected)/_components/mentor-sidebar.tsx twins
// (PORTING.md G5). Which entries it shows comes from sidebar-nav.ts, keyed by the
// edition the server resolved onto the `mentorPortal` shared prop.

interface MentorSidebarProps {
  edition: string | null;
  mentor: MentorProfile;
  discordGuildId: string | null;
  discordFaqChannelId: string | null;
}

export function MentorSidebar({
  edition,
  mentor,
  discordGuildId,
  discordFaqChannelId,
}: MentorSidebarProps) {
  const page = usePage();
  // `usePathname()` over there; Inertia's page url carries the query string.
  const pathname = page.url.split('?')[0];
  const navigation = buildNavigation(
    edition,
    discordGuildId,
    discordFaqChannelId,
  );
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const displayName =
    githubHandle(mentor.github) || page.props.auth.user?.name || 'Mentor';

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      await fetch('/api/auth/sign-out', {
        method: 'POST',
        headers: { 'X-CSRF-Token': csrfToken() },
      });
      window.location.href = '/login';
    } catch (error) {
      console.error('Logout error:', error);
      setIsLoggingOut(false);
    }
  };

  const itemClass = (active: boolean) =>
    cn(
      'flex items-center gap-3 border-2 px-3 py-2 font-mono text-sm transition-all',
      active
        ? 'border-primary bg-primary text-background'
        : 'border-transparent text-primary hover:border-primary hover:bg-primary/10',
    );

  return (
    <div className="flex h-full flex-col border-primary border-r-2 bg-background/80 backdrop-blur-sm">
      <div className="flex h-16 items-center border-primary border-b-2 px-4">
        <div
          className="aspect-[576/112] h-6 w-auto"
          style={{
            backgroundColor: 'hsl(var(--primary))',
            maskImage: 'url(/assets/logos/platanus.svg)',
            maskSize: 'contain',
            maskRepeat: 'no-repeat',
            maskPosition: 'center',
            WebkitMaskImage: 'url(/assets/logos/platanus.svg)',
            WebkitMaskSize: 'contain',
            WebkitMaskRepeat: 'no-repeat',
            WebkitMaskPosition: 'center',
          }}
        />
      </div>

      <nav className="flex-1 space-y-1 p-4">
        {navigation.map((item) => {
          if (item.external) {
            return (
              <a
                key={item.name}
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                className={itemClass(false)}
              >
                <item.icon className="h-5 w-5" />
                {item.name}
              </a>
            );
          }
          const isActive = isItemActive(pathname, item.href);
          return (
            <Link
              key={item.name}
              href={item.href}
              className={itemClass(isActive)}
            >
              <item.icon className="h-5 w-5" />
              {item.name}
            </Link>
          );
        })}
      </nav>

      <div className="border-primary border-t-2 p-4">
        <div className="space-y-3">
          <div className="flex items-center gap-2 border-2 border-primary/20 bg-primary/5 px-3 py-2">
            <User className="h-4 w-4 text-primary" />
            <span className="truncate font-mono text-primary text-sm">
              @{displayName}
            </span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleLogout}
            disabled={isLoggingOut}
            className="w-full border-2 border-primary font-mono text-primary hover:bg-primary hover:text-background"
          >
            <LogOut className="mr-2 h-4 w-4" />
            {isLoggingOut ? 'Cerrando...' : 'Cerrar sesión'}
          </Button>
        </div>
      </div>
    </div>
  );
}
